import React from 'react';
import ShortTextInput from './ShortTextInput';
import LongTextInput from './LongTextInput';
import YesNoInput from './YesNoInput';
import MultipleChoiceInput from './MultipleChoiceInput';
import DropdownInput from './DropdownInput';
import WelcomeInput from './WelcomeInput';
import PictureChoiceInput from './PictureChoiceInput';
import LegalInput from './LegalInput';
import RatingInput from './RatingInput';
import UploadMediaInput from './UploadMediaInput';
import EndScreenInput from './EndScreenInput';
import {Box, Typography} from '@mui/material';
import {AnyQuestion, QuestionType} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface QuestionInputProps {
  question: AnyQuestion;
  onQuestionUpdate: (question: AnyQuestion) => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

const QuestionInput: React.FC<QuestionInputProps> = ({
  question,
  onQuestionUpdate,
  isSelected = false,
  onSelect
}) => {
  const {t} = useTranslation();

  switch (question.type) {
    case QuestionType.WELCOME:
      return (
        <WelcomeInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.SHORT_TEXT:
      return (
        <ShortTextInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.LONG_TEXT:
      return (
        <LongTextInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.YES_NO:
      return (
        <YesNoInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.MULTIPLE_CHOICE:
      return (
        <MultipleChoiceInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.PICTURE_CHOICE:
      return (
        <PictureChoiceInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.DROPDOWN:
      return (
        <DropdownInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.LEGAL:
      return (
        <LegalInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.RATING:
      return (
        <RatingInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.UPLOAD_MEDIA:
      return (
        <UploadMediaInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    case QuestionType.END_SCREEN:
      return (
        <EndScreenInput
          question={question}
          onQuestionUpdate={onQuestionUpdate}
          isSelected={isSelected}
          onSelect={onSelect}
        />
      );

    default:
      // TypeScript exhaustiveness check - this should never be reached
      const _exhaustiveCheck: never = question;
      return (
        <Box
          onClick={onSelect}
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 1,
            border: isSelected ? '2px solid' : '1px solid',
            borderColor: isSelected ? 'primary.main' : 'divider',
            backgroundColor: isSelected ? 'action.selected' : 'background.paper',
            cursor: 'pointer'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('Unknown question type')}
          </Typography>
        </Box>
      );
  }
};

export default QuestionInput;
